import {
  AvailableFeaturesForPhase,
  AvailableProductFeatures,
  CubeReducerState,
  Phase,
  Price,
  Product,
  DisabledReason,
  DisabledReasonType,
  VisibleEnabledFeatureAvailable,
  CubeStatus,
  ResolvedPhase
} from 'modules/Cube/domain/cube.domain.types'
import { add, isAfter, startOfDay } from '@sequencehq/utils/dates'
import compose from 'lodash/fp/compose'
import { getEditQuoteAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/quote/getEditQuoteAvailable'
import {
  quoteStatusAdapter,
  scheduleStatusAdapter
} from 'modules/Cube/utils/status.adapter'
import { getIsAQuoteTemplate } from 'Cube/domain/postActionStages/utils/getIsQuoteATemplate'
import { calculateAbsoluteDatesForPhases } from '@sequencehq/utils'
import {
  amendmentLockReason,
  isLockedByAmendment
} from 'modules/Cube/domain/postActionStages/queries/availableFeatures/utils/amendmentLock'
import {
  isQuoteSuperseded,
  supersededQuoteReason
} from 'modules/Cube/domain/postActionStages/queries/availableFeatures/utils/quoteSuperseded'
import { getAddPhaseAvailable } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/common/getAddPhaseAvailable'
import { getIsAmendedScheduleCovered } from 'modules/Cube/domain/postActionStages/queries/getIsAmendedScheduleCovered'
import { isPhaseCompleted } from './isPhaseCompleted'

export const getCanAddProduct = (prevState: CubeReducerState) => {
  return ![
    CubeStatus.ScheduleCompleted,
    CubeStatus.ScheduleCancelled,
    CubeStatus.ScheduleSuspended
  ].includes(prevState.data.common.status)
}

export type ProductLevelCheckContext = {
  prevState: CubeReducerState
  resolvedPhase: ResolvedPhase
  orderedPhases: ResolvedPhase[]
  allResolvedPhases: Record<Phase['id'], ResolvedPhase>
  product: Product
  price?: Price
  today: Date
}
type ProductLevelAvailabilityCheck = (
  ctx: ProductLevelCheckContext
) => (prevCheckResults: Array<DisabledReason>) => Array<DisabledReason>

export type PhaseLevelCheckContext = {
  prevState: CubeReducerState
  resolvedPhase: ResolvedPhase
  orderedPhases: ResolvedPhase[]
  allResolvedPhases: Record<Phase['id'], ResolvedPhase>
  today: Date
}
type PhaseLevelAvailabilityCheck = (
  ctx: PhaseLevelCheckContext
) => (prevCheckResults: Array<DisabledReason>) => Array<DisabledReason>

export const evaluateChecksForProduct =
  (config: { visibleIfDisabled: boolean }) =>
  (
    ...checks: Array<
      ProductLevelAvailabilityCheck | PhaseLevelAvailabilityCheck
    >
  ) =>
  (ctx: ProductLevelCheckContext): VisibleEnabledFeatureAvailable => {
    const result = compose(...checks.map(check => check(ctx)))(
      []
    ) as Array<DisabledReason>

    return {
      available: {
        visible: config.visibleIfDisabled ? true : !result.length,
        enabled: !result.length
      },
      reasons: result.filter(Boolean)
    }
  }

/**
 * Evaluates the various checks in the context of a phase passed in.
 * @param visibleIfDisabled If true, the feature will be visible if disabled unless a reason matched the hideIfReason arrays.
 * @param hideIfReason An array of DisabledReasonTypes which will set visible to false if matched, even if visibleIfDisabled is true.
 * @returns
 */
export const evaluateChecksForPhase =
  (config: {
    visibleIfDisabled?: boolean
    hideIfReason?: Array<DisabledReasonType>
  }) =>
  (...checks: Array<PhaseLevelAvailabilityCheck>) =>
  (ctx: PhaseLevelCheckContext): VisibleEnabledFeatureAvailable => {
    const result = compose(...checks.map(check => check(ctx)))(
      []
    ) as Array<DisabledReason>

    const visibleIfNoMitigatingReason =
      config.visibleIfDisabled === true ? true : !result.length
    const reasonToHideFound =
      config.hideIfReason &&
      Boolean(result.find(r => config.hideIfReason?.includes(r.reasonType)))

    return {
      available: {
        visible: reasonToHideFound ? false : visibleIfNoMitigatingReason,
        enabled: !result.length
      },
      reasons: result.filter(Boolean)
    }
  }

/** Product level checks */
export const checkValidStatusForMinimumEditing: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (isLockedByAmendment(ctx.prevState)) {
      return [...prevCheckResults, amendmentLockReason(ctx.prevState)]
    }

    if (isQuoteSuperseded(ctx.prevState)) {
      return [...prevCheckResults, supersededQuoteReason(ctx.prevState)]
    }

    if (
      ctx.prevState.data.common.status === CubeStatus.QuoteDraft &&
      ctx.prevState.data.quote.runningQuoteApprovalAutomationExecutionId
    ) {
      return [
        ...prevCheckResults,
        { reasonType: DisabledReasonType.PendingApproval, metadata: {} }
      ]
    }

    if (
      ctx.prevState.data.common.status ===
      CubeStatus.QuotePreparingSignatureRequest
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType:
            DisabledReasonType.CantChangeWhenPreparingSignatureRequest,
          metadata: {}
        }
      ]
    }

    if (
      [
        CubeStatus.ScheduleCompleted,
        CubeStatus.ScheduleCancelled,
        CubeStatus.ScheduleSuspended
      ].includes(ctx.prevState.data.common.status)
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.InvalidStatus,
          metadata: {
            statusValue: ctx.prevState.data.common.status
          }
        }
      ]
    }

    return prevCheckResults
  }
export const disableIfNotValidStatusForPricingChanges: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (isLockedByAmendment(ctx.prevState)) {
      return [...prevCheckResults, amendmentLockReason(ctx.prevState)]
    }

    if (isQuoteSuperseded(ctx.prevState)) {
      return [...prevCheckResults, supersededQuoteReason(ctx.prevState)]
    }

    const editQuoteAvailable = getEditQuoteAvailable(ctx.prevState)
    if (!editQuoteAvailable.available.enabled) {
      return [...prevCheckResults, ...editQuoteAvailable.reasons]
    }

    if (
      [CubeStatus.ScheduleCompleted, CubeStatus.ScheduleCancelled].includes(
        ctx.prevState.data.common.status
      )
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.CompletedSchedule,
          metadata: {
            statusValue: ctx.prevState.data.common.status
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * Checks to see if the current phase is the first phase in the schedule.
 * If it is, disable.
 * @param ctx
 * @returns
 */
export const checkIsNotFirstPhase: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (ctx.orderedPhases[0].id === ctx.resolvedPhase.id) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.IsFirstPhase,
          metadata: {
            statusValue: ctx.prevState.data.common.status
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * Checks to see if the current phase is the first phase in the schedule.
 * If it is, disable.
 * @param ctx
 * @returns
 */
export const checkIsNotOnlyPhase: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (ctx.orderedPhases.length === 1) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.IsOnlyPhase,
          metadata: {
            statusValue: ctx.prevState.data.common.status
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * Checks to see if the current cube status is one of the statuses
 * that is valid for a schedule - this can be used as a proxy for
 * understanding if we are currently operating as a schedule.
 * @param ctx
 * @returns
 */
export const checkIsAScheduleStatus: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (!scheduleStatusAdapter.out(ctx.prevState.data.common.status)) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.InvalidStatus,
          metadata: {
            statusValue: ctx.prevState.data.common.status
          }
        }
      ]
    }

    return prevCheckResults
  }

export const featureEnabled =
  (
    feature: keyof CubeReducerState['configuration']['features']
  ): PhaseLevelAvailabilityCheck =>
  ctx =>
  prevCheckResults => {
    if (!ctx.prevState.configuration.features[feature]) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.FeatureDisabled,
          metadata: {
            feature
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * For a feature that a newer one replaces: it switches off as soon as the
 * newer feature is on, so the two never show at the same time.
 */
export const featureNotReplacedBy =
  (
    replacement: keyof CubeReducerState['configuration']['features']
  ): PhaseLevelAvailabilityCheck =>
  ctx =>
  prevCheckResults => {
    if (ctx.prevState.configuration.features[replacement]) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.FeatureDisabled,
          metadata: {
            replacedBy: replacement
          }
        }
      ]
    }

    return prevCheckResults
  }

export const checkIfMinimumAlreadyExists: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const minimumExists = ctx.resolvedPhase.minimums.length > 0

    if (minimumExists) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.MaxMinimums,
          metadata: {
            minimums: ctx.resolvedPhase.minimums.length
          }
        }
      ]
    }

    return prevCheckResults
  }

export const checkIfCreditGrantAlreadyExists: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const creditGrantExists = ctx.resolvedPhase.creditGrants.length > 0

    if (creditGrantExists) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.MaxCreditGrants,
          metadata: {
            creditGrants: ctx.resolvedPhase.creditGrants.length
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * A grant is scoped to the phase's prices, so there has to be at least one
 * product on the phase before you can add one.
 */
export const checkIfPhaseHasAnyPricesForCreditGrant: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const phaseHasPrices = ctx.resolvedPhase.prices.length > 0

    if (phaseHasPrices) {
      return prevCheckResults
    }

    return [
      ...prevCheckResults,
      {
        reasonType: DisabledReasonType.PhaseHasNoPrices,
        metadata: {}
      }
    ]
  }

export const checkIfAnyApplicablePricesForMinimum: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const applicablePrices = ctx.resolvedPhase.prices
      .filter(Boolean)
      .find(price => 'usageMetricId' in price.structure)

    if (!applicablePrices) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.NoValidPrices,
          metadata: {
            priceIds: ctx.resolvedPhase.prices.map(({ id }) => id)
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * For this check we are only interested in the active, persisted, start date for the phase.
 * @param ctx
 * @returns
 */
export const disableIfPhaseHasCompleted: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const persistedPhaseEndDate =
      ctx.prevState.initialQueries.resolvedPhases[ctx.resolvedPhase.id]?.dates
        .absolute.end

    if (
      isPhaseCompleted({
        scheduleStatus: ctx.prevState.data.common.status,
        persistedPhaseEndDate,
        today: ctx.today
      })
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.CompletedPhase,
          metadata: {
            phaseEndDate: persistedPhaseEndDate
          }
        }
      ]
    }

    return prevCheckResults
  }

export const disableIfBeforeAmendmentEffectiveFrom: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (ctx.resolvedPhase.isBeforeAmendmentEffectiveFrom) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.BeforeAmendmentEffectiveFrom,
          metadata: {
            effectiveFrom: ctx.prevState.data.quote.amendment?.effectiveFrom
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * Check to see if the price is an in advance price, and the phase in which it is contained
 * has begun.
 * @param ctx
 * @returns
 */
export const disableIfInAdvanceAndPhaseStarted: ProductLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    /**
     * If the price was not present when first loading, we asume it to have
     * been added and not yet committed, so therefore not part of the
     * started phase yet.
     */
    if (
      !ctx.price?.id ||
      !ctx.prevState.initialData?.prices[ctx.price?.id] ||
      !ctx.prevState.initialData.common.startDate
    ) {
      return prevCheckResults
    }

    const initialResolvedPhases = calculateAbsoluteDatesForPhases({
      phases: Object.values(ctx.prevState.initialData.phases).map(phase => ({
        ...phase,
        discounts: [],
        minimums: []
      })),
      startDate: ctx.prevState.initialData.common.startDate
    })

    const initialResolvedPhase = initialResolvedPhases.find(
      phase => phase.id === ctx.resolvedPhase.id
    )

    if (
      ctx.prevState.data.common.status === CubeStatus.ScheduleActive &&
      ctx.resolvedPhase.dates.absolute.start &&
      isAfter(
        startOfDay(ctx.today),
        startOfDay(
          initialResolvedPhase?.startDate ??
            ctx.resolvedPhase.dates.absolute.start
        )
      ) &&
      ctx.price?.billingType === 'IN_ADVANCE'
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.InvalidBillingType,
          metadata: {
            phaseStartDate: ctx.resolvedPhase.dates.absolute.start
          }
        }
      ]
    }

    return prevCheckResults
  }

/**
 * For this check we are only interested in the active, persisted, start date for the phase.
 * @param ctx
 * @returns
 */
export const checkAndDisableIfPhaseStartedAndActive: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const persistedPhaseStartDate =
      ctx.prevState.initialQueries.resolvedPhases[ctx.resolvedPhase.id]?.dates
        .absolute.start

    if (
      [
        CubeStatus.ScheduleActive,
        CubeStatus.ScheduleCompleted,
        CubeStatus.ScheduleCancelled
      ].includes(ctx.prevState.data.common.status) &&
      persistedPhaseStartDate &&
      ctx.today &&
      isAfter(ctx.today, persistedPhaseStartDate)
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.CompletedPhase,
          metadata: {
            phaseStartDate: persistedPhaseStartDate
          }
        }
      ]
    }

    return prevCheckResults
  }

export const checkIfPhaseStartedAndActive: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const persistedPhaseStartDate =
      ctx.prevState.initialQueries.resolvedPhases[ctx.resolvedPhase.id]?.dates
        .absolute.start

    if (
      [
        CubeStatus.ScheduleActive,
        CubeStatus.ScheduleCompleted,
        CubeStatus.ScheduleCancelled
      ].includes(ctx.prevState.data.common.status) &&
      persistedPhaseStartDate &&
      ctx.today &&
      isAfter(ctx.today, persistedPhaseStartDate)
    ) {
      return prevCheckResults
    }

    return [
      ...prevCheckResults,
      {
        reasonType: DisabledReasonType.PhaseNotStarted,
        metadata: {
          phaseStartDate: persistedPhaseStartDate
        }
      }
    ]
  }

export const checkIfGlobalDiscountAlreadyExists: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const globalDiscount = ctx.resolvedPhase.discounts.find(
      discount => discount.applyToAllPrices
    )
    const hasAvailableSpecificPrice = ctx.resolvedPhase.prices.some(
      price =>
        !ctx.resolvedPhase.discounts.some(
          discount =>
            !discount.applyToAllPrices && discount.priceIds.includes(price.id)
        )
    )

    if (
      ctx.prevState.configuration.features.stackedDiscounts &&
      (!globalDiscount || hasAvailableSpecificPrice)
    ) {
      return prevCheckResults
    }

    if (globalDiscount) {
      return [
        ...prevCheckResults,
        {
          reasonType: ctx.prevState.configuration.features.stackedDiscounts
            ? DisabledReasonType.NoValidDiscounts
            : DisabledReasonType.GlobalDiscountExists,
          metadata: {
            discountIds: ctx.resolvedPhase.discounts.map(({ id }) => id)
          }
        }
      ]
    }

    return prevCheckResults
  }

export const checkIfPhaseFirstPhase: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (ctx.orderedPhases[0].id !== ctx.resolvedPhase.id) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.NotFirstPhase,
          metadata: {}
        }
      ]
    }

    return prevCheckResults
  }

export const checkIfAnyInvalidProration: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const phaseIndex = ctx.orderedPhases.findIndex(
      p => p.id === ctx.resolvedPhase.id
    )
    const nextPhase = ctx.orderedPhases[phaseIndex + 1]

    if (!nextPhase) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.NoProration,
          metadata: {}
        }
      ]
    }

    if (
      !Object.values(nextPhase.analysis?.proration ?? {}).some(
        arr => arr.length
      )
    ) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.NoProration,
          metadata: {}
        }
      ]
    }

    return prevCheckResults
  }
export const disableIfGoCardlessEnabledAndOnlyOneProduct: ProductLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const goCardlessEnabled = ctx.prevState.data.schedule.goCardlessPayment
    const totalPricesAcrossAllPhases = Object.values(
      ctx.allResolvedPhases
    ).reduce((total, phase) => total + phase.prices.length, 0)

    if (goCardlessEnabled && totalPricesAcrossAllPhases <= 1) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.GoCardlessRequiresAtLeastOneProduct,
          metadata: {}
        }
      ]
    }

    return prevCheckResults
  }

/**
 * As these are a composition, the highest priority message should go at the end
 */
export const getPriceDeletionAvailableForProductInPhase =
  evaluateChecksForProduct({ visibleIfDisabled: true })(
    disableIfGoCardlessEnabledAndOnlyOneProduct,
    disableIfPhaseHasCompleted,
    disableIfInAdvanceAndPhaseStarted,
    disableIfNotValidStatusForPricingChanges,
    disableIfBeforeAmendmentEffectiveFrom
  )

export const getPriceEditingAvailableForProductInPhase =
  evaluateChecksForProduct({ visibleIfDisabled: true })(
    disableIfPhaseHasCompleted,
    disableIfInAdvanceAndPhaseStarted,
    disableIfNotValidStatusForPricingChanges,
    disableIfBeforeAmendmentEffectiveFrom
  )

export const getDiscountEditingAvailableForProductInPhase =
  evaluateChecksForProduct({ visibleIfDisabled: true })(
    disableIfPhaseHasCompleted,
    disableIfNotValidStatusForPricingChanges,
    disableIfBeforeAmendmentEffectiveFrom
  )

export const getDiscountDeletingAvailableForProductInPhase =
  evaluateChecksForProduct({ visibleIfDisabled: true })(
    disableIfPhaseHasCompleted,
    disableIfNotValidStatusForPricingChanges,
    disableIfBeforeAmendmentEffectiveFrom
  )

export const checkCanEditPricingModelInQuote: ProductLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const isQuote =
      Boolean(quoteStatusAdapter.out(ctx.prevState.data.common.status)) ||
      getIsAQuoteTemplate(ctx.prevState)

    const productIdsInPhases = Object.entries(ctx.allResolvedPhases).flatMap(
      ([, phase]) => phase.products.map(product => product.id)
    )

    const isProductIdAlreadySelected: boolean = productIdsInPhases.includes(
      ctx.product.id
    )

    if (isQuote && isProductIdAlreadySelected) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.CantEditPricingModelOnQuotePrice,
          metadata: {}
        }
      ]
    }

    return prevCheckResults
  }

export const checkScheduleIsActive: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (ctx.prevState.data.common.status !== CubeStatus.ScheduleActive) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.ScheduleNotActive,
          metadata: {}
        }
      ]
    }

    return prevCheckResults
  }

export const checkIfActiveMilestonePhase: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (
      ctx.resolvedPhase.dates.duration === 'MILESTONE' &&
      ctx.resolvedPhase.dates.absolute.start &&
      isAfter(ctx.today, ctx.resolvedPhase.dates.absolute.start)
    ) {
      return prevCheckResults
    }

    return [
      ...prevCheckResults,
      {
        reasonType: DisabledReasonType.NotMilestonePhase,
        metadata: {}
      }
    ]
  }

export const getCanEditPricingModelForProductInPhase = evaluateChecksForProduct(
  { visibleIfDisabled: true }
)(checkCanEditPricingModelInQuote)

export const disableIfMetadataEditingNotEnabled: ProductLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (!ctx.prevState.configuration.features.phasePriceMetadataEditing) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.FeatureDisabled,
          metadata: {
            feature: 'priceMetadataEditing'
          }
        }
      ]
    }

    return prevCheckResults
  }

export const getCanEditPriceMetadataForProductInPhase =
  evaluateChecksForProduct({ visibleIfDisabled: false })(
    disableIfMetadataEditingNotEnabled,
    disableIfPhaseHasCompleted,
    disableIfNotValidStatusForPricingChanges
  )

export const getProductFeatureAvailabilityForPhase = (
  ctx: ProductLevelCheckContext
): AvailableProductFeatures => {
  return {
    edit: getPriceEditingAvailableForProductInPhase(ctx),
    delete: getPriceDeletionAvailableForProductInPhase(ctx),
    editDiscount: getDiscountEditingAvailableForProductInPhase(ctx),
    deleteDiscount: getDiscountEditingAvailableForProductInPhase(ctx),
    canEditPricingModel: getCanEditPricingModelForProductInPhase(ctx),
    editPhasePriceMetadata: getCanEditPriceMetadataForProductInPhase(ctx)
  }
}

export const getEditMinimumAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  disableIfPhaseHasCompleted,
  checkValidStatusForMinimumEditing,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getAlignDatesAvailable = evaluateChecksForPhase({
  visibleIfDisabled: false
})(
  checkIfAnyInvalidProration,
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getDeleteMinimumAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  disableIfPhaseHasCompleted,
  checkValidStatusForMinimumEditing,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getAddMinimumAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  checkIfMinimumAlreadyExists,
  checkIfAnyApplicablePricesForMinimum,
  disableIfPhaseHasCompleted,
  checkValidStatusForMinimumEditing,
  disableIfBeforeAmendmentEffectiveFrom
)

/**
 * Checks run right to left, so the list reads bottom-up: whether the schedule
 * can be changed at all answers first and wins the reason a disabled button
 * shows, then "no products yet", then "already has one".
 */
export const getAddCreditGrantAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true,
  hideIfReason: [DisabledReasonType.FeatureDisabled]
})(
  featureEnabled('creditGrantsEditing'),
  checkIfCreditGrantAlreadyExists,
  checkIfPhaseHasAnyPricesForCreditGrant,
  disableIfBeforeAmendmentEffectiveFrom,
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges
)

export const getAddGlobalDiscountAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  checkIfGlobalDiscountAlreadyExists,
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getAddProductAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getStartDateEditingAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  checkAndDisableIfPhaseStartedAndActive,
  disableIfNotValidStatusForPricingChanges,
  checkIfPhaseFirstPhase,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getDurationEditingAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getMilestoneCompletionAvailable = evaluateChecksForPhase({
  visibleIfDisabled: false
})(
  checkIfActiveMilestonePhase,
  checkIfPhaseStartedAndActive,
  disableIfNotValidStatusForPricingChanges
)

export const getEditPhaseRecurrenceAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true,
  hideIfReason: [
    DisabledReasonType.IsFirstPhase,
    DisabledReasonType.FeatureDisabled,
    DisabledReasonType.InvalidStatus
  ]
})(
  disableIfNotValidStatusForPricingChanges,
  checkIsNotFirstPhase,
  checkAndDisableIfPhaseStartedAndActive,
  checkIsAScheduleStatus,
  featureEnabled('phaseRecurrenceEditing')
)

export const getEditPhaseNameAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true,
  hideIfReason: [DisabledReasonType.IsFirstPhase]
})(
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom
)

export const getEditContractTagAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true,
  hideIfReason: [DisabledReasonType.FeatureDisabled]
})(
  featureEnabled('contractTagEditing'),
  featureNotReplacedBy('contractReferenceEditing'),
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges
)

/**
 * References stay visible on a phase that can no longer be edited, so they can
 * still be read and followed.
 */
export const getEditContractReferencesAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true,
  hideIfReason: [DisabledReasonType.FeatureDisabled]
})(
  featureEnabled('contractReferenceEditing'),
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges
)

export const getDeletePhaseAvailable = evaluateChecksForPhase({
  visibleIfDisabled: true
})(
  disableIfNotValidStatusForPricingChanges,
  checkIsNotOnlyPhase,
  checkAndDisableIfPhaseStartedAndActive,
  disableIfBeforeAmendmentEffectiveFrom
)

/**
 * Reordering writes a new order into the presentation, so it is an edit like any other and has to
 * answer the same question as the rest: can this schedule or quote be changed at all. Only the
 * narrow pricing table draws the drag handle today, so a locked schedule was never reorderable in
 * practice - but that was the layout's doing, and this says it outright.
 */
export const getReorderProductsAvailable = evaluateChecksForPhase({
  visibleIfDisabled: false
})(
  disableIfPhaseHasCompleted,
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom
)
const phaseIndexIn = (ctx: PhaseLevelCheckContext) =>
  ctx.orderedPhases.findIndex(phase => phase.id === ctx.resolvedPhase.id)

/**
 * A new phase is a copy of the one it is inserted next to, so there has to be something to copy.
 * An empty phase would produce another empty phase, and the schedule would refuse to save.
 */
const noPricesToCopyFrom = (phase: ResolvedPhase): DisabledReason => ({
  reasonType: DisabledReasonType.NoValidPrices,
  metadata: { phase }
})

const disableIfPhaseHasNoPrices: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults =>
    ctx.resolvedPhase.prices.length
      ? prevCheckResults
      : [...prevCheckResults, noPricesToCopyFrom(ctx.resolvedPhase)]

/**
 * Inserting after a phase the amendment has locked would push the amendment's own phase off its
 * effective from date, so the gap in front of a locked phase has to stay shut.
 */
export const disableIfAfterLockedPhase: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const index = phaseIndexIn(ctx)
    const previousPhase = index > 0 ? ctx.orderedPhases[index - 1] : undefined

    if (previousPhase?.isBeforeAmendmentEffectiveFrom) {
      return [
        ...prevCheckResults,
        {
          reasonType: DisabledReasonType.BeforeAmendmentEffectiveFrom,
          metadata: {
            effectiveFrom: ctx.prevState.data.quote.amendment?.effectiveFrom
          }
        }
      ]
    }

    return prevCheckResults
  }

/** The quote's phases already reach the end of the schedule it amends, so a new one has no room. */
export const disableIfAmendedScheduleCovered: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    if (getIsAmendedScheduleCovered(ctx.prevState)) {
      return [
        ...prevCheckResults,
        { reasonType: DisabledReasonType.AmendedScheduleCovered, metadata: {} }
      ]
    }

    return prevCheckResults
  }

/**
 * Inserting after the last phase is the same thing as adding one on the end, so it answers to
 * `addPhase` and the rules that come with it - an open ended final phase, say. Anywhere else the
 * new phase goes in the gap, and the one it would sit in front of is the one being copied - and
 * the one pushed later, so it is the phase the started-and-active rule has to be asked about.
 */
const disableIfNothingToInsertAfterInto: PhaseLevelAvailabilityCheck =
  ctx => prevCheckResults => {
    const nextPhase = ctx.orderedPhases[phaseIndexIn(ctx) + 1]

    if (!nextPhase) {
      const addPhase = getAddPhaseAvailable({
        orderedPhases: ctx.orderedPhases
      })(ctx.prevState)

      return addPhase.available.enabled
        ? prevCheckResults
        : [...prevCheckResults, ...addPhase.reasons]
    }

    if (!nextPhase.prices.length) {
      return [...prevCheckResults, noPricesToCopyFrom(nextPhase)]
    }

    return compose(
      checkAndDisableIfPhaseStartedAndActive({
        ...ctx,
        resolvedPhase: nextPhase
      }),
      disableIfAmendedScheduleCovered(ctx)
    )(prevCheckResults) as Array<DisabledReason>
  }

/**
 * Both of these hide rather than disable: they sit in the phase menu alongside items that are
 * only ever offered when they can be used, and an insert nobody can explain reads as broken.
 *
 * A new phase takes the start date of the one it displaces, pushing that phase and everything
 * after it later - which is why `checkAndDisableIfPhaseStartedAndActive` guards both. It is the
 * same rule that stops a started phase's start date being edited, arrived at the other way round.
 */
export const getInsertPhaseBeforeAvailable = evaluateChecksForPhase({
  visibleIfDisabled: false
})(
  disableIfNotValidStatusForPricingChanges,
  checkAndDisableIfPhaseStartedAndActive,
  disableIfBeforeAmendmentEffectiveFrom,
  disableIfAfterLockedPhase,
  disableIfAmendedScheduleCovered,
  disableIfPhaseHasNoPrices
)

export const getInsertPhaseAfterAvailable = evaluateChecksForPhase({
  visibleIfDisabled: false
})(
  disableIfNotValidStatusForPricingChanges,
  disableIfBeforeAmendmentEffectiveFrom,
  disableIfNothingToInsertAfterInto
)

export const getAvailableFeaturesForPhases = (ctx: {
  resolvedPhases: Record<Phase['id'], ResolvedPhase>
  orderedPhases: ResolvedPhase[]
  prevState: CubeReducerState
}): Record<Phase['id'], AvailableFeaturesForPhase> => {
  const today = new Date()

  return Object.values(ctx.resolvedPhases).reduce(
    (
      phaseFeaturesAcc,
      resolvedPhase
    ): Record<Phase['id'], AvailableFeaturesForPhase> => {
      const phaseLevelCheckContext = {
        prevState: ctx.prevState,
        resolvedPhase,
        allResolvedPhases: ctx.resolvedPhases,
        orderedPhases: ctx.orderedPhases,
        today
      }

      const productLevelFeaturesForThisPhase: Record<
        Product['id'],
        AvailableProductFeatures
      > = Object.values(ctx.prevState.data.products).reduce(
        (productFeaturesAcc, product: Product) => ({
          ...productFeaturesAcc,
          [product.id]: getProductFeatureAvailabilityForPhase({
            ...phaseLevelCheckContext,
            product,
            price: resolvedPhase.prices.find(
              price => price.productId === product.id
            )
          })
        }),
        {}
      )

      const allProductDiscountUnavailableReasons = resolvedPhase.prices
        .flatMap(price => {
          const productId = price.productId
          return productLevelFeaturesForThisPhase[productId].editDiscount
            .reasons
        })
        .filter(Boolean)

      const globalDiscountEditingAvailable = {
        available: {
          visible: true,
          enabled: !allProductDiscountUnavailableReasons.length
        },
        reasons: allProductDiscountUnavailableReasons
      }

      const featuresForPhase: AvailableFeaturesForPhase = {
        products: productLevelFeaturesForThisPhase,
        phase: {
          editName: getEditPhaseNameAvailable(phaseLevelCheckContext),
          contractTag: getEditContractTagAvailable(phaseLevelCheckContext),
          contractReferences: getEditContractReferencesAvailable(
            phaseLevelCheckContext
          ),
          completeMilestone: getMilestoneCompletionAvailable(
            phaseLevelCheckContext
          ),
          discount: {
            edit: globalDiscountEditingAvailable,
            add: getAddGlobalDiscountAvailable(phaseLevelCheckContext),
            delete: globalDiscountEditingAvailable
          },
          creditGrant: {
            add: getAddCreditGrantAvailable(phaseLevelCheckContext)
          },
          product: {
            add: getAddProductAvailable(phaseLevelCheckContext),
            reorder: getReorderProductsAvailable(phaseLevelCheckContext)
          },
          insert: {
            before: getInsertPhaseBeforeAvailable(phaseLevelCheckContext),
            after: getInsertPhaseAfterAvailable(phaseLevelCheckContext)
          },
          minimum: {
            add: getAddMinimumAvailable(phaseLevelCheckContext),
            delete: getDeleteMinimumAvailable(phaseLevelCheckContext),
            edit: getEditMinimumAvailable(phaseLevelCheckContext)
          },
          dates: {
            alignDates: getAlignDatesAvailable(phaseLevelCheckContext),
            editStart: getStartDateEditingAvailable(phaseLevelCheckContext),
            editDuration: getDurationEditingAvailable(phaseLevelCheckContext)
          },
          editPhaseRecurrence: getEditPhaseRecurrenceAvailable(
            phaseLevelCheckContext
          ),
          delete: getDeletePhaseAvailable(phaseLevelCheckContext)
        }
      }
      return {
        ...phaseFeaturesAcc,
        [resolvedPhase.id]: featuresForPhase
      }
    },
    {}
  )
}
